import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X } from "lucide-react";
import { Link } from "wouter";
import { ClarikMark } from "@/components/clarik-mark";

/**
 * Clarik marketing home — v2 brand spec.
 *
 * Editorial layout: cream paper background, a quiet lowercase wordmark with
 * the signal dot, an oversized display headline set in Inter with tight
 * tracking, an italic Fraunces pull-quote, and a minimum of chrome.
 */

const THREE_THINGS = [
  {
    eyebrow: "find",
    title: "Find the engagement before the engagement finds you.",
    body: "Pull qualified prospects from Google Places, screenshots, or your own list. Enrich with Apollo, auto-draft the first email, and route to the rep most likely to close.",
  },
  {
    eyebrow: "book",
    title: "Run the show. Not the spreadsheet.",
    body: "Calendar, spreadsheet, list, and tour views over the same schedule. Double-booking guards. Routing-sheet PDFs. A public event-request page that drops straight into your pipeline.",
  },
  {
    eyebrow: "get paid",
    title: "Invoices, deposits, late fees — reconciled before the CFO asks.",
    body: "Stripe Connect, PayPal, talent commission splits, and a billing audit that closes the loop without anyone forwarding a receipt to anyone.",
  },
];

const CAPABILITIES = [
  {
    num: "01",
    name: "Find work",
    items: [
      "AI lead generation & prospecting",
      "Google Places + Apollo enrichment",
      "screenshot-to-lead AI import",
      "automatic lead scoring",
      "an AI first-responder that engages inbound leads before a rep touches them",
    ],
  },
  {
    num: "02",
    name: "Book it",
    items: [
      "Multi-view master schedule",
      "AI tour planner with route optimization",
      "double-booking guards",
      "public booking pages clients use without a login",
      "multi-talent, multi-slot events",
      "artist portal",
    ],
  },
  {
    num: "03",
    name: "Get paid",
    items: [
      "Automated invoicing from booked events",
      "late-fee scheduler",
      "direct pay through Stripe & PayPal",
      "deposit collection & automated payouts",
      "white-label payment portals",
      "commission splits & cash-flow analytics",
    ],
  },
  {
    num: "04",
    name: "Communicate",
    items: [
      "Gmail integration with AI reply drafting",
      "unified email + SMS inbox",
      "mass email",
      "multi-channel drip campaigns",
      "Twilio SMS",
      "trigger-based automations",
      "e-signature with audit trail",
    ],
  },
  {
    num: "05",
    name: "Let AI work",
    items: [
      "A role-aware voice assistant with read/write CRM access",
      "AI that qualifies leads, drafts replies & enriches contacts",
      "AI image generation for social",
      "an AI outbound caller in your own cloned voice",
    ],
  },
  {
    num: "06",
    name: "Marketing",
    items: [
      "Google, Meta, TikTok & LinkedIn ad management",
      "multi-touch attribution",
      "social post composer with approval workflows",
      "social calendar across Meta, TikTok & YouTube",
    ],
  },
];

const FOUNDER_STATS = [
  {
    label: "2024 · SPREADSHEETS",
    num: "$45K",
    sub: "Eight tools, zero context, and a lot of late nights.",
    accent: false,
  },
  {
    label: "2025 · BUILT CLARIK",
    num: "$145K",
    sub: "Built the foundation while running the business on it.",
    accent: true,
  },
  {
    label: "2026 · THROUGH MAY",
    num: "$100K",
    sub: "Already — and we're not through May yet.",
    accent: true,
  },
];

const BETA_URL = "https://clarik.io/beta";

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);

  const closeMobileMenu = () => {
    setMenuClosing(true);
    setTimeout(() => {
      setMobileMenuOpen(false);
      setMenuClosing(false);
    }, 180);
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--paper))] text-[hsl(var(--ink))]">
      {/* ── Nav ──────────────────────────────────────────────────────── */}
      <header className="border-b border-[hsl(var(--fog))] relative z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" data-testid="link-clarik-home">
            <ClarikMark className="text-lg" />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[hsl(var(--graphite))]">
            <Link href="/marketing/features" className="hover:text-[hsl(var(--ink))] transition-colors" data-testid="link-nav-product">Product</Link>
            <Link href="/pricing" className="hover:text-[hsl(var(--ink))] transition-colors" data-testid="link-nav-pricing">Pricing</Link>
            <Link href="/marketing/about" className="hover:text-[hsl(var(--ink))] transition-colors" data-testid="link-nav-about">About</Link>
          </nav>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              className="text-sm text-[hsl(var(--graphite))] px-3 sm:px-4"
              onClick={() => { window.location.href = "/login"; }}
              data-testid="button-sign-in-header"
            >
              Sign in
            </Button>
            <a
              href={BETA_URL}
              className="inline-flex items-center px-4 py-2 bg-[hsl(var(--green))] text-[hsl(var(--paper))] rounded-full text-sm font-medium hover:bg-[hsl(var(--green-hover))] transition-colors"
              data-testid="button-start-free"
            >
              Start free
            </a>
            {/* Hamburger — mobile only */}
            <button
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-md text-[hsl(var(--graphite))] hover:text-[hsl(var(--ink))] hover:bg-[hsl(var(--fog))] transition-colors"
              onClick={() => mobileMenuOpen ? closeMobileMenu() : setMobileMenuOpen(true)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              data-testid="button-mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* ── Mobile dropdown ──────────────────────────────────────── */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop — closes menu on outside tap */}
            <div
              className="fixed inset-0 z-40"
              aria-hidden="true"
              onClick={closeMobileMenu}
            />
            <nav
              className={`md:hidden absolute top-full left-0 right-0 z-50 bg-[hsl(var(--paper))] border-b border-[hsl(var(--fog))] shadow-md px-6 py-4 flex flex-col gap-1 overflow-hidden ${
                menuClosing
                  ? "animate-out fade-out slide-out-from-top-2 duration-[180ms]"
                  : "animate-in fade-in slide-in-from-top-2 duration-200"
              }`}
              data-testid="nav-mobile-menu"
            >
              <Link
                href="/marketing/features"
                className="block py-3 text-sm font-medium text-[hsl(var(--graphite))] hover:text-[hsl(var(--ink))] border-b border-[hsl(var(--fog))] transition-colors"
                onClick={closeMobileMenu}
                data-testid="link-mobile-nav-product"
              >
                Product
              </Link>
              <Link
                href="/pricing"
                className="block py-3 text-sm font-medium text-[hsl(var(--graphite))] hover:text-[hsl(var(--ink))] border-b border-[hsl(var(--fog))] transition-colors"
                onClick={closeMobileMenu}
                data-testid="link-mobile-nav-pricing"
              >
                Pricing
              </Link>
              <Link
                href="/marketing/about"
                className="block py-3 text-sm font-medium text-[hsl(var(--graphite))] hover:text-[hsl(var(--ink))] transition-colors"
                onClick={closeMobileMenu}
                data-testid="link-mobile-nav-about"
              >
                About
              </Link>
            </nav>
          </>
        )}
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-12">
        <p
          className="text-[13px] uppercase tracking-[0.08em] text-[hsl(var(--graphite))] font-mono mb-7"
          data-testid="text-hero-eyebrow"
        >
          The booking platform for people who{" "}
          <span className="text-[hsl(var(--green))]">/</span> call the shots
        </p>
        <h1
          className="text-display text-[clamp(2.75rem,7vw,5.25rem)] max-w-4xl leading-[0.98] tracking-[-0.045em]"
          data-testid="text-hero-headline"
        >
          Run the show.
          <br />
          <span className="text-[hsl(var(--green))]">Not the spreadsheet.</span>
        </h1>
        <p
          className="text-lg md:text-xl text-[hsl(var(--ink-soft))] mt-6 max-w-[560px] leading-[1.55]"
          data-testid="text-hero-subhead"
        >
          One platform for leads, engagements, contracts, invoices, payments, email, and a team of AI assistants that never sleep. Stop duct-taping eight tools together.
        </p>
        <div className="mt-9 flex flex-wrap gap-4 items-center">
          <a
            href={BETA_URL}
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-[hsl(var(--green))] text-[hsl(var(--paper))] rounded-full text-[15px] font-medium hover:bg-[hsl(var(--green-hover))] transition-colors"
            data-testid="button-hero-request-access"
          >
            Request access <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <p className="mt-4 text-sm text-[hsl(var(--ink-soft))]" data-testid="text-hero-offer">
          <strong className="font-medium text-[hsl(var(--green))]">Apply to be a Beta Tester</strong>{" "}
          - Get our $129/mn plan 4 months free, then $50/mo for life as a thank you.{" "}
          <span
            className="inline-block ml-1 px-2.5 py-0.5 bg-[hsl(var(--green-soft))] text-[hsl(var(--green))] rounded-full text-xs font-medium"
            data-testid="badge-hero-scarcity"
          >
            Only 10 spots per business type
          </span>
        </p>

        {/* ── Engagements screenshot mock ────────────────────────────── */}
        <div
          className="mt-12 rounded-2xl bg-[hsl(var(--ink))] p-2 max-w-[1100px]"
          data-testid="mock-engagements-board"
        >
          <div className="rounded-xl bg-[hsl(var(--paper-soft))] overflow-hidden">
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[hsl(var(--fog))]">
              <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--fog))]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--fog))]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--fog))]" />
              <span className="ml-auto text-xs font-mono text-[hsl(var(--graphite))]">
                22 active <span className="text-[hsl(var(--green))]">●</span>
              </span>
            </div>
            <p className="px-5 pt-4 pb-2 text-[11px] uppercase tracking-[0.08em] font-mono text-[hsl(var(--graphite))]">
              Engagements · November
            </p>
            <div className="divide-y divide-[hsl(var(--fog))] text-sm">
              {[
                { date: "Nov 02", event: "The Saint Lucia Sessions", venue: "Riverside Hall", status: "Confirmed", statusGreen: true, price: "$4,200" },
                { date: "Nov 08", event: "Late Night Quartet", venue: "Blue Door", status: "Deposit pending", statusGreen: false, price: "$2,800" },
                { date: "Nov 14", event: "Midwest Tour — Night 3", venue: "Vertex Theatre", status: "Routing", statusGreen: false, price: "$6,500" },
                { date: "Nov 22", event: "Private — Reyes Wedding", venue: "Hotel Ardent", status: "Confirmed", statusGreen: true, price: "$8,150" },
              ].map((row, i) => (
                <div key={i} className="px-5 py-3.5">
                  {/* Mobile: stacked */}
                  <div className="md:hidden">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex items-baseline gap-3 min-w-0">
                        <span className="text-[hsl(var(--graphite))] font-mono text-xs shrink-0">{row.date}</span>
                        <span className="font-medium truncate">{row.event}</span>
                      </div>
                      <span className="font-mono font-medium shrink-0">{row.price}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                      <span className="text-[hsl(var(--graphite))] truncate">{row.venue}</span>
                      <span className={`font-mono uppercase ${row.statusGreen ? "text-[hsl(var(--green))]" : "text-[hsl(var(--graphite))]"}`}>{row.status}</span>
                    </div>
                  </div>
                  {/* Desktop: 12-column grid */}
                  <div className="hidden md:grid grid-cols-12 items-center gap-3">
                    <span className="col-span-2 font-mono text-[hsl(var(--graphite))]">{row.date}</span>
                    <span className="col-span-4 font-medium">{row.event}</span>
                    <span className="col-span-3 text-[hsl(var(--graphite))]">{row.venue}</span>
                    <span className={`col-span-2 font-mono text-xs ${row.statusGreen ? "text-[hsl(var(--green))]" : "text-[hsl(var(--graphite))]"}`}>{row.status}</span>
                    <span className="col-span-1 text-right font-mono font-medium">{row.price}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Divider ──────────────────────────────────────────────────── */}
      <div className="border-t border-[hsl(var(--fog))]" />

      {/* ── "Built for" strip ─────────────────────────────────────────── */}
      <section
        className="max-w-6xl mx-auto px-6 py-14 border-b border-[hsl(var(--fog))]"
        data-testid="section-built-for"
      >
        <p className="text-[11px] uppercase tracking-[0.12em] font-mono text-[hsl(var(--graphite))] mb-6">Built for</p>
        <div className="flex flex-wrap gap-x-10 gap-y-3 text-[28px] md:text-[32px] font-medium tracking-tight">
          {[
            "talent agencies",
            "booking offices",
            "tour managers",
            "venue buyers",
            "indie reps",
            "production companies",
          ].map((cat) => (
            <span key={cat} data-testid={`built-for-${cat.replace(/\s+/g, "-")}`}>
              {cat}
            </span>
          ))}
        </div>
      </section>

      {/* ── Three things, done properly ─────────────────────────────── */}
      <section id="tour" className="max-w-6xl mx-auto px-6 py-20 border-b border-[hsl(var(--fog))]" data-testid="section-three-things">
        <p className="text-[13px] uppercase tracking-[0.08em] font-mono text-[hsl(var(--graphite))] mb-4">the loop</p>
        <h2 className="text-display text-4xl md:text-5xl leading-tight tracking-[-0.03em] mb-12">
          Three things, done properly.
        </h2>
        <div className="grid md:grid-cols-3 gap-x-12 gap-y-12">
          {THREE_THINGS.map((p, i) => (
            <article key={p.eyebrow} data-testid={`section-thing-${p.eyebrow.replace(/\s+/g, "-")}`}>
              <p className="font-mono text-[hsl(var(--green))] text-xs mb-2">
                0{i + 1} · {p.eyebrow.toUpperCase()}
              </p>
              <h3 className="text-[22px] font-medium tracking-[-0.02em] leading-snug mb-3">{p.title}</h3>
              <p className="text-[hsl(var(--ink-soft))] text-[15px] leading-relaxed">{p.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Under the Hood — capabilities wall ──────────────────────── */}
      <section
        className="bg-[#EFE9D8] border-b border-[hsl(var(--fog))]"
        data-testid="section-capabilities-wall"
      >
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-[13px] uppercase tracking-[0.08em] font-mono text-[hsl(var(--graphite))] mb-4">Under the hood</p>
          <h2 className="text-display text-4xl md:text-5xl leading-tight tracking-[-0.03em] mb-12 max-w-2xl">
            You'd need eight subscriptions to do what Clarik does in one.
          </h2>
          <div>
            {CAPABILITIES.map((bucket, i) => (
              <div
                key={bucket.num}
                className={`py-6 grid md:grid-cols-[190px_1fr] gap-4 md:gap-8 items-start border-t border-[hsl(var(--fog))] ${
                  i === CAPABILITIES.length - 1 ? "border-b border-[hsl(var(--fog))]" : ""
                }`}
                data-testid={`bucket-${bucket.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-baseline gap-2.5">
                  <span className="font-mono text-[12px] text-[hsl(var(--green))]">{bucket.num}</span>
                  <span className="text-[20px] font-medium tracking-[-0.02em]">{bucket.name}</span>
                </div>
                <p className="text-[15px] leading-[1.7] text-[hsl(var(--ink-soft))]">
                  {bucket.items.map((item, j) => (
                    <span key={j}>
                      {item}
                      {j < bucket.items.length - 1 && (
                        <span className="text-[#C9C2AC] mx-1">·</span>
                      )}
                    </span>
                  ))}
                </p>
              </div>
            ))}
          </div>
          <p
            className="text-center mt-10 font-italic-display text-2xl tracking-[-0.015em] text-[hsl(var(--ink))]"
            data-testid="text-short-list"
          >
            And that's the short list.
          </p>
        </div>
      </section>

      {/* ── Who it's for ────────────────────────────────────────────── */}
      <section
        className="max-w-6xl mx-auto px-6 py-20"
        data-testid="section-who-its-for"
      >
        <p className="text-[13px] uppercase tracking-[0.08em] font-mono text-[hsl(var(--graphite))] mb-4">who it's for</p>
        <h2 className="text-display text-4xl md:text-5xl leading-tight tracking-[-0.03em] mb-12 max-w-2xl">
          Built for the people who call the shots.
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              eyebrow: "solo talent & reps",
              title: "Own your roster of one.",
              body: "A working artist with a couple of regular venues and a side of corporate gigs. Engagements, invoices, and email in one tab — no spreadsheet rituals.",
            },
            {
              eyebrow: "talent agencies",
              title: "Move a real roster.",
              body: "Multi-artist, multi-show, multi-talent bookings with double-booking guards, tour routing, and per-artist billing splits. Your bookers stop forwarding emails to each other.",
            },
            {
              eyebrow: "venues & booking offices",
              title: "Close the loop on every date.",
              body: "Public event-request capture, contracts that get signed, deposits that actually arrive, and a master schedule the whole front-of-house team can read.",
            },
          ].map((p) => (
            <article
              key={p.eyebrow}
              className="rounded-xl border border-[hsl(var(--fog))] bg-[hsl(var(--paper-soft))] p-7"
              data-testid={`card-who-${p.eyebrow.replace(/\s+/g, "-").replace(/&/g, "and")}`}
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--green))] mb-3">
                {p.eyebrow}
              </p>
              <h3 className="text-[19px] font-medium tracking-[-0.02em] mb-2.5">{p.title}</h3>
              <p className="text-[hsl(var(--graphite))] text-sm leading-relaxed">{p.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Founder Note ─────────────────────────────────────────────── */}
      <section className="bg-[hsl(var(--ink))] text-[hsl(var(--paper))] py-24" data-testid="section-founder">
        <div className="max-w-4xl mx-auto px-6">
          <p
            className="text-[13px] uppercase tracking-[0.08em] font-mono text-[#5DCAA5] mb-7"
            data-testid="text-founder-label"
          >
            From the founder
          </p>
          <blockquote
            className="font-italic-display text-[clamp(1.6rem,3.5vw,2.125rem)] leading-[1.3] tracking-[-0.015em] mb-10 text-[hsl(var(--paper))]"
            data-testid="text-founder-quote"
          >
            "I'm not going to show you testimonials I don't have yet. I'll show you my own numbers. I built Clarik because I needed it — and it's already changing what a year looks like for me."
          </blockquote>
          <div className="grid md:grid-cols-3 gap-8 mb-9 max-w-2xl" data-testid="grid-founder-stats">
            {FOUNDER_STATS.map((stat) => (
              <div key={stat.label} data-testid={`stat-${stat.label.split("·")[0].trim().toLowerCase()}`}>
                <p className="font-mono text-[11px] tracking-[0.08em] text-[#C9C2AC] mb-2">{stat.label}</p>
                <p className={`text-[40px] font-medium tracking-[-0.03em] leading-none mb-1 ${stat.accent ? "text-[#5DCAA5]" : "text-[hsl(var(--paper))]"}`}>
                  {stat.num}
                </p>
                <p className="text-[13px] text-[#C9C2AC]">{stat.sub}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3.5" data-testid="row-founder-author">
            <div
              className="w-11 h-11 rounded-full bg-[hsl(var(--green))] text-[hsl(var(--paper))] flex items-center justify-center font-medium text-sm"
              data-testid="avatar-founder"
            >
              JF
            </div>
            <div>
              <p className="text-[14px] font-medium" data-testid="text-founder-name">Josh Farley</p>
              <p className="text-[13px] text-[#C9C2AC]" data-testid="text-founder-title">Founder, Clarik</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Closer ──────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-28 text-center border-t border-[hsl(var(--fog))]" data-testid="section-closing">
        <h2 className="text-display text-[clamp(2.25rem,5vw,3.5rem)] leading-[1.05] tracking-[-0.035em] max-w-3xl mx-auto mb-5">
          The booking platform you actually want to open on a Monday.
        </h2>
        <p className="text-sm text-[hsl(var(--ink-soft))] mb-8" data-testid="text-closing-offer">
          <strong className="font-medium text-[hsl(var(--green))]">4 months free,</strong>{" "}
          then $50/mo for life.{" "}
          <span
            className="inline-block ml-1 px-2.5 py-0.5 bg-[hsl(var(--green-soft))] text-[hsl(var(--green))] rounded-full text-xs font-medium"
            data-testid="badge-closing-scarcity"
          >
            Only 10 founding spots per business type
          </span>
        </p>
        <a
          href={BETA_URL}
          className="inline-flex items-center gap-2 px-7 py-4 bg-[hsl(var(--green))] text-[hsl(var(--paper))] rounded-full text-[15px] font-medium hover:bg-[hsl(var(--green-hover))] transition-colors"
          data-testid="button-closer-request-access"
        >
          Request access <ArrowRight className="h-4 w-4" />
        </a>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-[hsl(var(--fog))] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-xs text-[hsl(var(--graphite))]">
          <div className="flex items-center gap-2">
            <ClarikMark className="text-sm" />
            <span>© {new Date().getFullYear()} Clarik. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <a href={BETA_URL} className="hover:underline" data-testid="footer-link-help">Help</a>
            <a href={BETA_URL} className="hover:underline" data-testid="footer-link-security">Security</a>
            <a href={BETA_URL} className="hover:underline" data-testid="footer-link-trust">Trust</a>
            <a href={BETA_URL} className="hover:underline" data-testid="footer-link-terms">Terms</a>
            <a href={BETA_URL} className="hover:underline" data-testid="footer-link-privacy">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
